import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, Send } from "lucide-react";

const fmt = (s) => { try { return new Date(s).toLocaleString("id-ID"); } catch { return s; } };

function Thread({ other, msgs, me, onSend, sending }) {
  const [isi, setIsi] = useState("");
  return (
    <div className="glass rounded-2xl p-4" data-testid={`pesan-thread-${other.id}`}>
      <p className="font-semibold text-emerald-900 dark:text-emerald-100 mb-3 flex items-center gap-2"><MessageSquare className="h-4 w-4" /> {other.nama}</p>
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1 mb-3">
        {msgs.map((m) => {
          const mine = m.dari_staff_id === me;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`pesan-row-${m.id}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-emerald-600 text-white rounded-br-sm" : "bg-white/70 dark:bg-white/10 text-emerald-950 dark:text-emerald-50 rounded-bl-sm"}`}>
                <p className="whitespace-pre-wrap">{m.isi}</p>
                <p className={`text-[10px] mt-1 ${mine ? "text-emerald-50/80" : "text-slate-400 dark:text-slate-400"}`}>{fmt(m.created_at)}</p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-2">
        <Input data-testid={`pesan-reply-input-${other.id}`} value={isi} onChange={(e) => setIsi(e.target.value)} placeholder="Tulis balasan..." className="flex-1 h-10" onKeyDown={(e) => { if (e.key === "Enter" && isi.trim()) { onSend(other.id, isi, () => setIsi("")); } }} />
        <Button data-testid={`pesan-reply-send-${other.id}`} className="h-10 gap-1.5 bg-emerald-700 hover:bg-emerald-800" disabled={sending || !isi.trim()} onClick={() => onSend(other.id, isi, () => setIsi(""))}><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

export default function PesanManager() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const me = user?.staff_id;
  const isPembimbing = user?.perms?.includes("pesan.kirim") || user?.perms?.includes("*");

  const { data: pesan = [] } = useQuery({ queryKey: ["pesan"], queryFn: async () => (await api.get("/pesan")).data });
  const { data: petugas = [] } = useQuery({ enabled: isPembimbing, queryKey: ["pesan-petugas"], queryFn: async () => (await api.get("/pesan/petugas-list")).data });

  const [ke, setKe] = useState("");
  const [isi, setIsi] = useState("");

  const send = useMutation({
    mutationFn: async ({ ke_staff_id, isi }) => api.post("/pesan", { ke_staff_id, isi }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pesan"] }); qc.invalidateQueries({ queryKey: ["pesan-unread"] }); },
    onError: (e) => toast.error(errMsg(e, "Gagal mengirim")),
  });

  const doSend = (ke_staff_id, text, clear) => { if (!text || !text.trim()) return; send.mutate({ ke_staff_id, isi: text }, { onSuccess: () => { clear && clear(); } }); };

  const threads = {};
  for (const m of pesan) {
    const other = m.dari_staff_id === me ? { id: m.ke_staff_id, nama: m.ke_nama } : { id: m.dari_staff_id, nama: m.dari_nama };
    (threads[other.id] || (threads[other.id] = { other, msgs: [] })).msgs.push(m);
  }
  const threadList = Object.values(threads);
  const unitLabel = (p) => p.assigned_ribath ? `Ribath ${p.assigned_ribath}` : p.assigned_departemen ? `Dept. ${p.assigned_departemen}` : "belum ada unit";

  return (
    <div className="space-y-5" data-testid="pesan-manager">
      <PageHeader icon={MessageSquare} title="Pesan Pembinaan"
        subtitle={isPembimbing ? "Kirim arahan ke Petugas Absensi. Mereka dapat membalas — jadi percakapan dua arah." : "Catatan & arahan dari Pembimbing. Anda dapat membalas langsung."} />

      {isPembimbing && (
        <div className="glass rounded-2xl p-5 space-y-3" data-testid="pesan-compose">
          <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">Mulai / Kirim Pesan Baru</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Kepada (Petugas Absensi)</Label>
              <Select value={ke} onValueChange={setKe}>
                <SelectTrigger data-testid="pesan-target"><SelectValue placeholder="Pilih petugas" /></SelectTrigger>
                <SelectContent>{petugas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama} · {unitLabel(p)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Isi Pesan</Label>
              <Textarea data-testid="pesan-isi" value={isi} onChange={(e) => setIsi(e.target.value)} placeholder="Tulis arahan / catatan pembinaan..." className="min-h-[44px]" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button data-testid="pesan-kirim" onClick={() => doSend(ke, isi, () => setIsi(""))} disabled={send.isPending || !ke || !isi.trim()} className="gap-2 rounded-xl bg-emerald-700 hover:bg-emerald-800"><Send className="h-4 w-4" /> Kirim</Button>
          </div>
        </div>
      )}

      {threadList.length === 0 ? (
        <div className="glass rounded-2xl p-8 text-center text-sm text-slate-400" data-testid="pesan-empty">Belum ada percakapan.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="pesan-list">
          {threadList.map((t) => <Thread key={t.other.id} other={t.other} msgs={t.msgs} me={me} onSend={doSend} sending={send.isPending} />)}
        </div>
      )}
    </div>
  );
}
